import { createContext, useContext, useState, useEffect } from 'react';
const Ctx = createContext(null);

// Get CSRF token from DOM if available
function getCsrfToken() {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

async function req(path, method = 'GET', body) {
  const token = localStorage.getItem('vt');
  const headers = token ? { Authorization: `Token ${token}` } : {};
  
  // Add CSRF token if available (for non-token auth requests)
  const csrfToken = getCsrfToken();
  if (csrfToken && !token) {
    headers['X-CSRFToken'] = csrfToken;
  }
  
  // Don't set Content-Type for FormData - let the browser handle it
  if (!(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  const opts = { method, headers, credentials: 'include' };
  if (body) {
    opts.body = body instanceof FormData ? body : JSON.stringify(body);
  }
  
  const r = await fetch(`/api${path}`, opts);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw d;
  return d;
}

export { req as api };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshUser = async (knownProfileData = null) => {
    const t = localStorage.getItem('vt');
    if (!t) return null;
    try {
      const profile = knownProfileData || await req('/matrimony/profile/');
      const prev = JSON.parse(localStorage.getItem('vu') || '{}');
      const fullName = (profile.first_name || profile.last_name)
        ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
        : (prev.full_name || prev.username || 'User');

      const updated = {
        ...prev,
        full_name: fullName,
        is_verified: !!profile.is_verified,
        is_subscriber: !!profile.is_active_subscriber,
        membership_tier: profile.membership_tier || 'free',
        membership_expires_at: profile.membership_expires_at || null,
        is_search_active: !!profile.is_search_active,
        phone: profile.phone || prev.phone,
      };
      localStorage.setItem('vu', JSON.stringify(updated));
      setUser(updated);
      return updated;
    } catch (e) {
      console.warn('Could not refresh live user profile:', e);
      return null;
    }
  };

  useEffect(() => {
    const t = localStorage.getItem('vt');
    const u = localStorage.getItem('vu');
    if (t && u) {
      try {
        setUser(JSON.parse(u));
      } catch {}
      // Fetch live user status from backend
      refreshUser().finally(() => setReady(true));
    } else {
      setReady(true);
    }
  }, []);

  const login = async (username, password) => {
    const d = await req('/matrimony/auth/login/', 'POST', { username, password });
    localStorage.setItem('vt', d.token);
    localStorage.setItem('vu', JSON.stringify(d));
    setUser(d);
    await refreshUser();
    return d;
  };

  const register = async (body) => {
    const d = await req('/matrimony/auth/register/', 'POST', body);
    localStorage.setItem('vt', d.token);
    localStorage.setItem('vu', JSON.stringify(d));
    setUser(d);
    await refreshUser();
    return d;
  };

  const logout = async () => {
    try {
      await req('/matrimony/auth/logout/', 'POST');
    } catch {}
    localStorage.removeItem('vt');
    localStorage.removeItem('vu');
    setUser(null);
  };

  return (
    <Ctx.Provider value={{ user, login, register, logout, refreshUser, ready }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
